import React, { useState, useEffect, useCallback } from 'react';
import { crewsAPI } from '../../services/api';
import { formatPhone } from '../../utils/phone';
import './crews.css';

const blankCrew = () => ({
    name: '',
    leadName: '',
    leadPhone: '',
    isActive: true,
    notes: '',
    members: [],
});

// Crews are the field teams work orders get texted to. The lead's phone is the one
// the system actually uses — it receives the work order and is where photos texted
// back are matched from — so the form calls that out rather than treating it as one
// contact field among many.
const CrewsPage = () => {
    const [crews, setCrews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null); // crew object being edited, or null
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');

    const fetchCrews = useCallback(async () => {
        try {
            setLoading(true);
            const res = await crewsAPI.getAll(false);
            setCrews(res.data);
            setError('');
        } catch (err) {
            console.error('Error fetching crews:', err);
            setError('Could not load crews.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCrews(); }, [fetchCrews]);

    const startCreate = () => setEditing(blankCrew());
    const startEdit = (crew) => setEditing({
        ...crew,
        leadName: crew.leadName || '',
        leadPhone: crew.leadPhone || '',
        notes: crew.notes || '',
        members: (crew.members || []).map(m => ({ ...m, phone: m.phone || '', role: m.role || '' })),
    });

    const handleSave = async () => {
        if (!editing?.name?.trim()) {
            setError('Crew name is required.');
            return;
        }
        try {
            setSaving(true);
            const payload = {
                name: editing.name.trim(),
                leadName: editing.leadName?.trim() || null,
                leadPhone: editing.leadPhone?.trim() || null,
                isActive: editing.isActive,
                notes: editing.notes?.trim() || null,
                members: (editing.members || [])
                    .filter(m => m.name?.trim())
                    .map(m => ({ id: m.id ?? null, name: m.name.trim(), phone: m.phone?.trim() || null, role: m.role?.trim() || null })),
            };
            if (editing.id) await crewsAPI.update(editing.id, payload);
            else await crewsAPI.create(payload);

            setEditing(null);
            setError('');
            fetchCrews();
        } catch (err) {
            console.error('Error saving crew:', err);
            setError(err.response?.data?.message || 'Could not save crew.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (crew) => {
        const warning = crew.assignedProjects > 0
            ? `\n\n${crew.assignedProjects} job${crew.assignedProjects === 1 ? '' : 's'} will be left unassigned (the jobs themselves are kept).`
            : '';
        if (!window.confirm(`Delete "${crew.name}"?${warning}\n\nDeactivating instead keeps it on its jobs and just hides it from the assignment list.`)) return;

        try {
            await crewsAPI.delete(crew.id);
            fetchCrews();
        } catch (err) {
            console.error('Error deleting crew:', err);
            setError('Could not delete crew.');
        }
    };

    const toggleActive = async (crew) => {
        try {
            await crewsAPI.update(crew.id, {
                name: crew.name,
                leadName: crew.leadName,
                leadPhone: crew.leadPhone,
                isActive: !crew.isActive,
                notes: crew.notes,
                members: (crew.members || []).map(m => ({ id: m.id, name: m.name, phone: m.phone, role: m.role })),
            });
            fetchCrews();
        } catch (err) {
            console.error('Error toggling crew:', err);
            setError('Could not update crew.');
        }
    };

    // --- member editing within the form ---
    const addMember = () => setEditing(e => ({ ...e, members: [...(e.members || []), { name: '', phone: '', role: '' }] }));
    const updateMember = (idx, field, value) => setEditing(e => ({
        ...e,
        members: e.members.map((m, i) => (i === idx ? { ...m, [field]: value } : m)),
    }));
    const removeMember = (idx) => setEditing(e => ({ ...e, members: e.members.filter((_, i) => i !== idx) }));

    const terms = search.trim().toLowerCase();
    const visible = terms
        ? crews.filter(c => [c.name, c.leadName, c.leadPhone, ...(c.members || []).map(m => m.name)]
            .filter(Boolean).join(' ').toLowerCase().includes(terms))
        : crews;

    return (
        <div className="crews-page">
            <div className="crews-header">
                <div>
                    <h1 className="crews-title"><span className="crews-title-icon">👷</span> Crews</h1>
                    <p className="crews-subtitle">
                        Install teams that receive work orders by text. The lead's phone is what gets
                        texted and what photo replies are matched against.
                    </p>
                </div>
                <button className="crews-btn-primary" onClick={startCreate}>+ Add Crew</button>
            </div>

            {error && <div className="crews-error">{error}</div>}

            <div className="crews-search">
                <input
                    type="search"
                    placeholder="Search crews by name, lead, phone, or member…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search crews"
                />
            </div>

            {loading ? (
                <div className="crews-empty">Loading crews…</div>
            ) : visible.length === 0 ? (
                <div className="crews-empty">
                    {crews.length === 0
                        ? 'No crews yet. Add one to start dispatching work orders by text.'
                        : `No crews match “${search}”.`}
                </div>
            ) : (
                <div className="crews-grid">
                    {visible.map(crew => (
                        <div key={crew.id} className={`crew-card ${crew.isActive ? '' : 'inactive'}`}>
                            <div className="crew-card-head">
                                <div>
                                    <h3 className="crew-name">{crew.name}</h3>
                                    <div className="crew-lead">
                                        {crew.leadName || 'No lead set'}
                                        {crew.leadPhone
                                            ? <span className="crew-phone"> · {formatPhone(crew.leadPhone)}</span>
                                            : <span className="crew-phone-missing"> · no phone — won't receive texts</span>}
                                    </div>
                                </div>
                                <span className={`crew-badge ${crew.isActive ? 'active' : 'inactive'}`}>
                                    {crew.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>

                            {crew.members?.length > 0 && (
                                <ul className="crew-members">
                                    {crew.members.map(m => (
                                        <li key={m.id}>
                                            {m.name}
                                            {m.role && <span className="crew-member-role"> — {m.role}</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {crew.notes && <p className="crew-notes">{crew.notes}</p>}

                            <div className="crew-card-foot">
                                <span className="crew-assigned">
                                    {crew.assignedProjects} job{crew.assignedProjects === 1 ? '' : 's'}
                                </span>
                                <div className="crew-actions">
                                    <button onClick={() => startEdit(crew)}>Edit</button>
                                    <button onClick={() => toggleActive(crew)}>
                                        {crew.isActive ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button className="crew-delete" onClick={() => handleDelete(crew)}>Delete</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div className="crew-modal-backdrop" onClick={() => !saving && setEditing(null)}>
                    <div className="crew-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{editing.id ? 'Edit Crew' : 'Add Crew'}</h2>

                        <label className="crew-field">
                            <span>Crew Name *</span>
                            <input
                                value={editing.name}
                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                placeholder="e.g. Eduardo's Crew"
                                autoFocus
                            />
                        </label>

                        <div className="crew-field-row">
                            <label className="crew-field">
                                <span>Lead Name</span>
                                <input
                                    value={editing.leadName}
                                    onChange={(e) => setEditing({ ...editing, leadName: e.target.value })}
                                    placeholder="e.g. Eduardo"
                                />
                            </label>
                            <label className="crew-field">
                                <span>Lead Phone</span>
                                <input
                                    value={editing.leadPhone}
                                    onChange={(e) => setEditing({ ...editing, leadPhone: e.target.value })}
                                    placeholder="(615) 555-0147"
                                />
                                <small>Work orders text this number, and photos sent from it file to the job.</small>
                            </label>
                        </div>

                        <div className="crew-members-editor">
                            <div className="crew-members-head">
                                <span>Crew Members</span>
                                <button type="button" onClick={addMember}>+ Add Member</button>
                            </div>
                            {(editing.members || []).length === 0 && (
                                <p className="crew-members-empty">No members added. The lead alone is enough to dispatch work.</p>
                            )}
                            {(editing.members || []).map((m, idx) => (
                                <div key={idx} className="crew-member-row">
                                    <input
                                        value={m.name}
                                        onChange={(e) => updateMember(idx, 'name', e.target.value)}
                                        placeholder="Name"
                                    />
                                    <input
                                        value={m.phone}
                                        onChange={(e) => updateMember(idx, 'phone', e.target.value)}
                                        placeholder="Phone (optional)"
                                    />
                                    <input
                                        value={m.role}
                                        onChange={(e) => updateMember(idx, 'role', e.target.value)}
                                        placeholder="Role"
                                    />
                                    <button type="button" className="crew-member-remove" onClick={() => removeMember(idx)}>✕</button>
                                </div>
                            ))}
                        </div>

                        <label className="crew-field">
                            <span>Notes</span>
                            <textarea
                                rows={2}
                                value={editing.notes}
                                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                placeholder="Anything the office should know about this crew"
                            />
                        </label>

                        <label className="crew-checkbox">
                            <input
                                type="checkbox"
                                checked={editing.isActive}
                                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                            />
                            <span>Active — available to assign to jobs</span>
                        </label>

                        <div className="crew-modal-actions">
                            <button onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
                            <button className="crews-btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : 'Save Crew'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CrewsPage;
